import { Check } from 'lucide-react';
import Card from '@/components/elements/Card';
import { useSearchParams } from 'react-router-dom';
import { FaSpinner } from 'react-icons/fa';
import { useUser } from '@/state/UserContext';
import type { PricingPlan } from '@/types/pricing.types';
import ContactButton from '@/components/ContactButton';

interface PricingCardProps {
  plan: PricingPlan;
  background: 'grey' | 'white';
}

function PricingCard({ plan, background }: PricingCardProps) {
  const { geoLocation, isUserLoading } = useUser();
  const [searchParams] = useSearchParams();
  const showPrice = searchParams.get('showPrice') === 'true';

  function getCurrencySymbol() {
    if (geoLocation?.country === 'GB') return '£';
    if (['AT', 'BE', 'DE', 'ES', 'FI', 'FR', 'GR', 'IE', 'IT', 'LU', 'NL', 'PT'].includes(geoLocation?.country || ''))
      return '€';
    return '$';
  }

  function getPrice() {
    if (!plan.price) return null;

    if (geoLocation?.country === 'GB') return plan.price.gbp;
    if (['AT', 'BE', 'DE', 'ES', 'FI', 'FR', 'GR', 'IE', 'IT', 'LU', 'NL', 'PT'].includes(geoLocation?.country || ''))
      return plan.price.eur;
    return plan.price.usd;
  }

  const price = getCurrencySymbol() + getPrice();

  return (
    <Card
      title={plan.name}
      background={background === 'grey' ? 'white' : 'grey'}
      style={plan.mostPopular ? { border: '2px solid #4CC95A' } : undefined}
    >
      {showPrice && (
        <div className='mt-4'>
          <strong className='text-4xl font-extrabold tracking-tight'>
            {isUserLoading ? <FaSpinner className='inline animate-spin max-h-[32px]' /> : price}
          </strong>{' '}
          <span> / month / seat</span>
        </div>
      )}

      <p
        className={`text-[#525252] tracking-tight text-[1.125rem]/[1.35rem] font-regular ${showPrice ? 'mt-4' : 'mt-2'}`}
      >
        {plan.description}
      </p>

      {plan.mostPopular && (
        <span className='absolute top-[-10px] left-[50%] translate-x-[-50%] bg-[#7AD817] uppercase text-white text-[.75rem] font-bold px-3 py-[3px] pt-[4px] rounded-full'>
          Most Popular
        </span>
      )}

      {plan.listHeading && <p className='mt-4 font-bold'>{plan.listHeading}</p>}

      <ul className='mt-3 mb-5 flex flex-col gap-3'>
        {plan.listItems.map((item, index) => (
          <li key={index} className='leading-[1.175rem] flex'>
            <Check className='block size-5 min-w-5 text-[#7AD817]' />
            <p className='pl-2' dangerouslySetInnerHTML={{ __html: item }} />
          </li>
        ))}
      </ul>

      <div className='mt-auto'>
        <ContactButton className='w-full' variant={!plan.mostPopular ? 'hollow' : 'gradient'} />
      </div>
    </Card>
  );
}

export default PricingCard;
